import { PaginationState } from "@tanstack/react-table"
import { useMemo } from "react"
import {
  type Query as CubeQuery,
  type TimeDimension,
  type BinaryFilter,
} from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import useDate from "~/ui-rtk/hooks/date"
import { getPivotData } from "~/ui-rtk/utils/cube"
import { MetricsMapping } from "~/ui-rtk/constants/metrics-mapping"
import { calculateConversionRates } from "./mockData"
import { TSortingState } from "~/ui-rtk/hooks/url-sorting"

/**
 * ============================================================================
 * CONSTANTS
 * ============================================================================
 */

const USE_MOCK_DATA = false
const COLLECTION_FUNNEL_CUBE = "company_web_analytics_collection_funnel"

/**
 * Valid column keys that support server-side sorting.
 * Calculated columns (conversion rates, abandonment rates) are excluded as they require
 * disableSorting: true in the table configuration.
 */
type SortableColumnKey =
  | "collectionTitle"
  | "collectionHandle"
  | "collectionViewers"
  | "addersToCart"
  | "purchasers"

/**
 * Maps UI column keys (camelCase) to Cube.js dimension/measure names (snake_case).
 * Extracted to module scope to prevent recreation on every render.
 *
 * Must stay in sync with the columns in CollectionFunnelTable that are NOT
 * flagged `disableSorting` — the table renders a clickable header for those but
 * sorts nothing itself (manualSorting), so a column missing from here is a
 * header that silently does nothing. `sortable-columns.test.ts` enforces the
 * pairing in both directions.
 */
export const SORT_KEY_MAPPING: Record<SortableColumnKey, string> = {
  collectionTitle: "collection_title",
  collectionHandle: "collection_handle",
  collectionViewers: "collection_viewers",
  addersToCart: "adders_to_cart",
  purchasers: "purchasers",
} as const

/**
 * ============================================================================
 * TYPE DEFINITIONS
 * ============================================================================
 */

export type SessionFilter = "TOTAL" | "ORGANIC" | "PAID" | "TRIGGERED"

/**
 * CollectionFunnelData interface includes all metrics from the API response.
 */
export interface CollectionFunnelData {
  collectionViewers: number
  addersToCart: number
  purchasers: number
  collectionToCartConversion: number
  cartToPurchaseConversion: number
  collectionToPurchaseConversion: number
  collectionToCartAbandon: number
  cartToPurchaseAbandon: number
  collectionToPurchaseAbandon: number
  totalRevenue: number
  avgOrderValue: number
  firstOrders: number
  repeatOrders: number
  revenueAtCart: number
  revenueAtPurchase: number
}

/**
 * CollectionTableRow represents the complete data model for each collection row.
 *
 * **Field Usage:**
 * - **UI Display**: collectionTitle, collectionHandle, collectionViewers, addersToCart,
 *   purchasers, and conversion rate fields (collectionToCartConversion, cartToPurchaseConversion, etc.)
 * - **Internal Calculations**: Revenue, LTV, and contribution fields are used for calculating
 *   conversion rates, opportunity costs, and other derived metrics
 * - **API Contract**: All fields are part of the Cube.js API response and should be kept
 *   for backward compatibility and data consistency
 *
 * @internal The following fields are used for calculations but not directly displayed in UI:
 * - sumFirstOrderRevs through sumFifthPlusOrderRevs (revenue metrics)
 * - avgFirstOrderLtv90Day through avgFifthPlusOrderLtv90Day (LTV metrics)
 * - sumFirstOrderPreMktgContribution through sumFifthPlusOrderPreMktgContribution (contribution metrics)
 */
export interface CollectionTableRow {
  collectionHandle: string
  collectionTitle: string
  collectionId: string
  collectionViewers: number
  addersToCart: number
  purchasers: number
  collectionToCartConversion: number
  collectionToCartAbandon: number
  cartToPurchaseConversion: number
  cartToPurchaseAbandon: number
  collectionToPurchaseConversion: number
  collectionToPurchaseAbandon: number
  firstOrders: number
  repeatOrders: number
  sumFirstOrderRevs: number
  sumSecondOrderRevs: number
  sumThirdOrderRevs: number
  sumFourthOrderRevs: number
  sumFifthPlusOrderRevs: number
  avgFirstOrderLtv90Day: number
  avgSecondOrderLtv90Day: number
  avgThirdOrderLtv90Day: number
  avgFourthOrderLtv90Day: number
  avgFifthPlusOrderLtv90Day: number
  sumFirstOrderPreMktgContribution: number
  sumSecondOrderPreMktgContribution: number
  sumThirdOrderPreMktgContribution: number
  sumFourthOrderPreMktgContribution: number
  sumFifthPlusOrderPreMktgContribution: number
  estimatedRevenue: number
  estimatedPerViewValue: number
}

/**
 * ============================================================================
 * MOCK DATA GENERATOR - Simple and clear
 * ============================================================================
 */
function generateMockFunnelData(
  sessionFilter: SessionFilter,
): CollectionFunnelData {
  const multipliers: Record<SessionFilter, number> = {
    TOTAL: 1.0,
    ORGANIC: 0.45,
    PAID: 0.35,
    TRIGGERED: 0.2,
  }

  const m = multipliers[sessionFilter]
  const viewers = Math.round(15420 * m)
  const addToCart = Math.round(2316 * m)
  const purchasers = Math.round(1158 * m)

  const conversionRates = calculateConversionRates(
    viewers,
    addToCart,
    purchasers,
  )

  const avgOrderValue = 127.5
  const totalRevenue = purchasers * avgOrderValue
  const firstOrders = Math.round(purchasers * 0.62)
  const repeatOrders = purchasers - firstOrders

  return {
    collectionViewers: viewers,
    addersToCart: addToCart,
    purchasers,
    ...conversionRates,
    totalRevenue,
    avgOrderValue,
    firstOrders,
    repeatOrders,
    revenueAtCart: addToCart * avgOrderValue,
    revenueAtPurchase: totalRevenue,
  }
}

function generateMockTableData(
  sessionFilter: SessionFilter,
): CollectionTableRow[] {
  const collections = [
    { id: "1", handle: "best-sellers", title: "Best Sellers" },
    { id: "2", handle: "new-arrivals", title: "New Arrivals" },
    { id: "3", handle: "summer-collection", title: "Summer Collection" },
    { id: "4", handle: "winter-sale", title: "Winter Sale" },
    { id: "5", handle: "accessories", title: "Accessories" },
    { id: "6", handle: "mens-collection", title: "Men's Collection" },
    { id: "7", handle: "womens-collection", title: "Women's Collection" },
    { id: "8", handle: "kids-collection", title: "Kids Collection" },
  ]

  const multipliers: Record<SessionFilter, number> = {
    TOTAL: 1.0,
    ORGANIC: 0.45,
    PAID: 0.35,
    TRIGGERED: 0.2,
  }

  const m = multipliers[sessionFilter]

  return collections.map((collection, index) => {
    const baseViewers = Math.round((3000 - index * 300) * m)
    const addToCart = Math.round(baseViewers * (0.12 + Math.random() * 0.08))
    const purchasers = Math.round(addToCart * (0.45 + Math.random() * 0.15))
    const conversionRates = calculateConversionRates(
      baseViewers,
      addToCart,
      purchasers,
    )
    const firstOrders = Math.round(purchasers * 0.6)
    const repeatOrders = purchasers - firstOrders
    const avgOrderValue = 85 + Math.random() * 40

    return {
      collectionHandle: collection.handle,
      collectionTitle: collection.title,
      collectionId: collection.id,
      collectionViewers: baseViewers,
      addersToCart: addToCart,
      purchasers,
      ...conversionRates,
      firstOrders,
      repeatOrders,
      sumFirstOrderRevs: firstOrders * avgOrderValue * 0.4,
      sumSecondOrderRevs: repeatOrders * avgOrderValue * 0.25,
      sumThirdOrderRevs: repeatOrders * avgOrderValue * 0.15,
      sumFourthOrderRevs: repeatOrders * avgOrderValue * 0.1,
      sumFifthPlusOrderRevs: repeatOrders * avgOrderValue * 0.1,
      avgFirstOrderLtv90Day: avgOrderValue * 1.2,
      avgSecondOrderLtv90Day: avgOrderValue * 1.5,
      avgThirdOrderLtv90Day: avgOrderValue * 1.8,
      avgFourthOrderLtv90Day: avgOrderValue * 2.0,
      avgFifthPlusOrderLtv90Day: avgOrderValue * 2.5,
      sumFirstOrderPreMktgContribution: firstOrders * avgOrderValue * 0.3,
      sumSecondOrderPreMktgContribution: repeatOrders * avgOrderValue * 0.2,
      sumThirdOrderPreMktgContribution: repeatOrders * avgOrderValue * 0.15,
      sumFourthOrderPreMktgContribution: repeatOrders * avgOrderValue * 0.1,
      sumFifthPlusOrderPreMktgContribution: repeatOrders * avgOrderValue * 0.1,
      estimatedRevenue: 0,
      estimatedPerViewValue: 0,
    }
  })
}

/**
 * ============================================================================
 * MAIN HOOK
 * ============================================================================
 */
export default function useConnect(
  sessionFilter: SessionFilter,
  paginationState?: PaginationState,
  dateRange?: (string | undefined)[],
  sorting?: TSortingState[],
  compareRange?: (string | undefined)[],
  /** When true, only fetch summary (for funnel chart). Table uses Chart/metrics-mapping. */
  summaryOnly = false,
  additionalTableFilters?: BinaryFilter[],
) {
  const currentCompany = useCurrentCompany()
  const { dateRange: fallbackDateRange } = useMinMaxDateRange()
  const dayjs = useDate()

  const companyId = currentCompany?.id || ""
  // Use provided dateRange or fall back to useMinMaxDateRange
  const effectiveDateRange =
    dateRange && dateRange[0] && dateRange[1]
      ? [dateRange[0], dateRange[1]]
      : [
          dayjs(fallbackDateRange[0]).format("YYYY-MM-DD"),
          dayjs(fallbackDateRange[1]).format("YYYY-MM-DD"),
        ]
  const startDate = effectiveDateRange[0]
  const endDate = effectiveDateRange[1]

  // Handle comparison date range
  const effectiveCompareRange =
    compareRange && compareRange[0] && compareRange[1]
      ? [compareRange[0], compareRange[1]]
      : undefined
  const compareStartDate = effectiveCompareRange?.[0]
  const compareEndDate = effectiveCompareRange?.[1]
  const isComparing = Boolean(compareStartDate && compareEndDate)

  // ============================================================================
  // MOCK MODE
  // ============================================================================
  if (USE_MOCK_DATA) {
    return {
      isLoading: false,
      funnelData: generateMockFunnelData(sessionFilter),
      tableData: generateMockTableData(sessionFilter),
      totalCount: 8,
    }
  }

  // ============================================================================
  // REAL MODE - Use metrics mapping
  // ============================================================================

  const countQuery = useMemo(() => {
    const filters: BinaryFilter[] = []

    // Add company filter
    filters.push({
      member: `${COLLECTION_FUNNEL_CUBE}.companyid`,
      operator: "equals",
      values: [companyId],
    })

    // Add session_filter dimension filter
    filters.push({
      member: `${COLLECTION_FUNNEL_CUBE}.session_filter`,
      operator: "equals",
      values: [sessionFilter],
    })

    if (additionalTableFilters?.length) {
      filters.push(...additionalTableFilters)
    }

    return {
      dimensions: [`${COLLECTION_FUNNEL_CUBE}.collection_id`],
      filters,
      timeDimensions: [
        {
          dimension: `${COLLECTION_FUNNEL_CUBE}.md_date`,
          dateRange: [startDate, endDate],
        },
      ],
    } as CubeQuery
  }, [sessionFilter, companyId, startDate, endDate, additionalTableFilters])

  // Queries with comparison support
  // Build summary query with comparison support using compareDateRange
  const summaryQuery = useMemo(() => {
    const baseQuery = MetricsMapping.collection_funnel_summary.q as CubeQuery
    const additionalFilters: BinaryFilter[] = []

    additionalFilters.push({
      member: `${COLLECTION_FUNNEL_CUBE}.companyid`,
      operator: "equals",
      values: [companyId],
    })

    additionalFilters.push({
      member: `${COLLECTION_FUNNEL_CUBE}.session_filter`,
      operator: "equals",
      values: [sessionFilter],
    })

    // Use compareDateRange if comparing, otherwise use dateRange
    const timeDimensions = baseQuery.timeDimensions?.map(
      (td: TimeDimension) => {
        if (isComparing && compareStartDate && compareEndDate) {
          return {
            ...td,
            compareDateRange: [
              [compareStartDate, compareEndDate],
              [startDate, endDate],
            ],
          }
        }
        return {
          ...td,
          dateRange: [startDate, endDate],
        }
      },
    )

    return {
      ...baseQuery,
      filters: [...(baseQuery.filters || []), ...additionalFilters],
      timeDimensions,
    } as CubeQuery
  }, [
    isComparing,
    compareStartDate,
    compareEndDate,
    startDate,
    endDate,
    sessionFilter,
    companyId,
  ])

  // Build table query with comparison support using compareDateRange
  const tableQuery = useMemo(() => {
    const baseQuery = MetricsMapping.collection_funnel_table.q as CubeQuery
    const filters: BinaryFilter[] = []

    filters.push({
      member: `${COLLECTION_FUNNEL_CUBE}.companyid`,
      operator: "equals",
      values: [companyId],
    })

    filters.push({
      member: `${COLLECTION_FUNNEL_CUBE}.session_filter`,
      operator: "equals",
      values: [sessionFilter],
    })

    if (additionalTableFilters?.length) {
      filters.push(...additionalTableFilters)
    }

    // Use compareDateRange if comparing, otherwise use dateRange
    const timeDimensions = baseQuery.timeDimensions?.map(
      (td: TimeDimension) => {
        if (isComparing && compareStartDate && compareEndDate) {
          return {
            ...td,
            compareDateRange: [
              [compareStartDate, compareEndDate],
              [startDate, endDate],
            ],
          }
        }
        return {
          ...td,
          dateRange: [startDate, endDate],
        }
      },
    )

    const query = {
      ...baseQuery,
      filters: [...(baseQuery.filters || []), ...filters],
      timeDimensions,
    } as CubeQuery

    // Apply pagination
    if (paginationState) {
      query.limit = paginationState.pageSize
      query.offset = paginationState.pageIndex * paginationState.pageSize
    }

    // Apply sorting
    if (sorting?.[0]) {
      const sort = sorting[0]
      const mappedKey = SORT_KEY_MAPPING[sort.id as SortableColumnKey]
      if (mappedKey) {
        query.order = {
          [`${COLLECTION_FUNNEL_CUBE}.${mappedKey}`]: sort.desc
            ? "desc"
            : "asc",
        }
      }
    }

    return query
  }, [
    isComparing,
    compareStartDate,
    compareEndDate,
    startDate,
    endDate,
    sessionFilter,
    companyId,
    paginationState,
    sorting,
    additionalTableFilters,
  ])

  // Fetch data (comparison data is included in the same result set with compareDateRange)
  const {
    resultSet: summaryResultSet,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useCubeQuery(summaryQuery)
  const {
    resultSet: tableResultSet,
    isLoading: isTableLoading,
    error: tableError,
  } = useCubeQuery(tableQuery, { skip: summaryOnly })
  const { resultSet: countResultSet, isLoading: isCountLoading } = useCubeQuery(
    countQuery,
    { skip: summaryOnly },
  )

  // Process summary data
  // Helper function to process funnel data from raw data array
  const processFunnelData = (data: Array<Record<string, unknown>>) => {
    const totals = data.reduce(
      (
        acc: {
          collectionViewers: number
          addersToCart: number
          purchasers: number
          firstOrders: number
          repeatOrders: number
          totalRevenue: number
        },
        row: Record<string, unknown>,
      ) => ({
        collectionViewers:
          acc.collectionViewers +
          Number(row[`${COLLECTION_FUNNEL_CUBE}.collection_viewers`] || 0),
        addersToCart:
          acc.addersToCart +
          Number(row[`${COLLECTION_FUNNEL_CUBE}.adders_to_cart`] || 0),
        purchasers:
          acc.purchasers +
          Number(row[`${COLLECTION_FUNNEL_CUBE}.purchasers`] || 0),
        firstOrders:
          acc.firstOrders +
          Number(row[`${COLLECTION_FUNNEL_CUBE}.first_orders`] || 0),
        repeatOrders:
          acc.repeatOrders +
          Number(row[`${COLLECTION_FUNNEL_CUBE}.repeat_orders`] || 0),
        totalRevenue:
          acc.totalRevenue +
          Number(row[`${COLLECTION_FUNNEL_CUBE}.total_revenue`] || 0),
      }),
      {
        collectionViewers: 0,
        addersToCart: 0,
        purchasers: 0,
        firstOrders: 0,
        repeatOrders: 0,
        totalRevenue: 0,
      },
    )

    const conversionRates = calculateConversionRates(
      totals.collectionViewers,
      totals.addersToCart,
      totals.purchasers,
    )

    // Calculate average order value - return 0 if no purchasers to avoid misleading metrics
    const avgOrderValue =
      totals.purchasers > 0 ? totals.totalRevenue / totals.purchasers : 0

    return {
      collectionViewers: totals.collectionViewers,
      addersToCart: totals.addersToCart,
      purchasers: totals.purchasers,
      ...conversionRates,
      totalRevenue: totals.totalRevenue,
      avgOrderValue,
      firstOrders: totals.firstOrders,
      repeatOrders: totals.repeatOrders,
      revenueAtCart: totals.addersToCart * avgOrderValue,
      revenueAtPurchase: totals.totalRevenue,
    }
  }

  const funnelData = useMemo<CollectionFunnelData | null>(() => {
    if (!summaryResultSet) return null

    // When using compareDateRange, access loadResponses
    const resultSetWithResponses = summaryResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      isComparing &&
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 1
    ) {
      // loadResponses[1] = current period data
      const currentData = resultSetWithResponses.loadResponses[1]?.data || []
      return processFunnelData(currentData)
    }

    // Non-comparison mode - use regular pivot data
    const pivotData = getPivotData(summaryResultSet, summaryQuery)
    return processFunnelData(pivotData)
  }, [summaryResultSet, summaryQuery, isComparing])

  // Process comparison summary data (from loadResponses[0])
  const compareFunnelData = useMemo<CollectionFunnelData | null>(() => {
    if (!isComparing || !summaryResultSet) {
      return null
    }

    // When using compareDateRange, access loadResponses
    const resultSetWithResponses = summaryResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 0
    ) {
      // loadResponses[0] = comparison period data
      const comparisonData = resultSetWithResponses.loadResponses[0]?.data || []
      return processFunnelData(comparisonData)
    }

    return null
  }, [isComparing, summaryResultSet])

  // Helper function to process table row data
  const processTableRow = (
    row: Record<string, unknown>,
  ): CollectionTableRow => {
    const collectionViewers = Number(
      row[`${COLLECTION_FUNNEL_CUBE}.collection_viewers`] || 0,
    )
    const addersToCart = Number(
      row[`${COLLECTION_FUNNEL_CUBE}.adders_to_cart`] || 0,
    )
    const purchasers = Number(row[`${COLLECTION_FUNNEL_CUBE}.purchasers`] || 0)
    const firstOrders = Number(
      row[`${COLLECTION_FUNNEL_CUBE}.first_orders`] || 0,
    )
    const repeatOrders = Number(
      row[`${COLLECTION_FUNNEL_CUBE}.repeat_orders`] || 0,
    )

    const conversionRates = calculateConversionRates(
      collectionViewers,
      addersToCart,
      purchasers,
    )

    return {
      collectionHandle: String(
        row[`${COLLECTION_FUNNEL_CUBE}.collection_handle`] || "",
      ),
      collectionTitle: String(
        row[`${COLLECTION_FUNNEL_CUBE}.collection_title`] ||
          "Unnamed Collection",
      ),
      collectionId: String(
        row[`${COLLECTION_FUNNEL_CUBE}.collection_id`] || "",
      ),
      collectionViewers,
      addersToCart,
      purchasers,
      ...conversionRates,
      firstOrders,
      repeatOrders,
      // Revenue metrics from cube
      sumFirstOrderRevs: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.sum_first_order_revs`] || 0,
      ),
      sumSecondOrderRevs: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.sum_second_order_revs`] || 0,
      ),
      sumThirdOrderRevs: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.sum_third_order_revs`] || 0,
      ),
      sumFourthOrderRevs: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.sum_fourth_order_revs`] || 0,
      ),
      sumFifthPlusOrderRevs: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.sum_fifth_plus_order_revs`] || 0,
      ),
      avgFirstOrderLtv90Day: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.avg_first_order_ltv_90_day`] || 0,
      ),
      avgSecondOrderLtv90Day: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.avg_second_order_ltv_90_day`] || 0,
      ),
      avgThirdOrderLtv90Day: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.avg_third_order_ltv_90_day`] || 0,
      ),
      avgFourthOrderLtv90Day: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.avg_fourth_order_ltv_90_day`] || 0,
      ),
      avgFifthPlusOrderLtv90Day: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.avg_fifth_plus_order_ltv_90_day`] || 0,
      ),
      sumFirstOrderPreMktgContribution: Number(
        row[
          `${COLLECTION_FUNNEL_CUBE}.sum_first_order_pre_mktg_contribution`
        ] || 0,
      ),
      sumSecondOrderPreMktgContribution: Number(
        row[
          `${COLLECTION_FUNNEL_CUBE}.sum_second_order_pre_mktg_contribution`
        ] || 0,
      ),
      sumThirdOrderPreMktgContribution: Number(
        row[
          `${COLLECTION_FUNNEL_CUBE}.sum_third_order_pre_mktg_contribution`
        ] || 0,
      ),
      sumFourthOrderPreMktgContribution: Number(
        row[
          `${COLLECTION_FUNNEL_CUBE}.sum_fourth_order_pre_mktg_contribution`
        ] || 0,
      ),
      sumFifthPlusOrderPreMktgContribution: Number(
        row[
          `${COLLECTION_FUNNEL_CUBE}.sum_fifth_plus_order_pre_mktg_contribution`
        ] || 0,
      ),
      estimatedRevenue: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.estimated_revenue`] || 0,
      ),
      estimatedPerViewValue: Number(
        row[`${COLLECTION_FUNNEL_CUBE}.estimated_per_view_value`] || 0,
      ),
    }
  }

  // Process table data
  const tableData = useMemo<CollectionTableRow[]>(() => {
    if (!tableResultSet) return []

    // When using compareDateRange, access loadResponses
    const resultSetWithResponses = tableResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      isComparing &&
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 1
    ) {
      // loadResponses[1] = current period data
      const currentData = resultSetWithResponses.loadResponses[1]?.data || []
      return currentData.map((row: Record<string, unknown>) =>
        processTableRow(row),
      )
    }

    // Non-comparison mode - use regular pivot data
    const pivotData = getPivotData(tableResultSet, tableQuery)
    return pivotData.map((row: Record<string, unknown>) => processTableRow(row))
  }, [tableResultSet, tableQuery, isComparing])

  const totalCount = useMemo(() => {
    if (!countResultSet) return 0
    const pivotData = getPivotData(countResultSet, countQuery)
    return pivotData.length
  }, [countResultSet, countQuery])

  // Process comparison table data (from loadResponses[0])
  const compareTableData = useMemo<CollectionTableRow[] | undefined>(() => {
    if (!isComparing || !tableResultSet) {
      return undefined
    }

    // When using compareDateRange, access loadResponses
    const resultSetWithResponses = tableResultSet as unknown as {
      loadResponses?: Array<{ data?: Array<Record<string, unknown>> }>
    }

    if (
      resultSetWithResponses.loadResponses &&
      resultSetWithResponses.loadResponses.length > 0
    ) {
      // loadResponses[0] = comparison period data
      const comparisonData = resultSetWithResponses.loadResponses[0]?.data || []
      return comparisonData.map((row: Record<string, unknown>) =>
        processTableRow(row),
      )
    }

    return undefined
  }, [isComparing, tableResultSet])

  return {
    isLoading: isSummaryLoading || isTableLoading || isCountLoading,
    error: summaryError || tableError,
    funnelData,
    compareFunnelData,
    tableData,
    compareTableData,
    totalCount,
    /** The exact request behind `tableData` — the CSV export re-runs it as-is. */
    tableQuery,
  }
}
